/**
 * Resource Types Tab
 * Manages ResourceType entries using a modal for creation/editing
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import ResourceTypeModal from '../components/ResourceTypeModal';
import { useResourceTypes } from '../hooks';

/**
 * Resource Types Tab Component
 * Displays and manages resource types with modal-based editing
 */
function ResourceTypesTab() {
  const { resourceTypes, isLoading, error, create, update, remove, isCreating, isUpdating, isDeleting } = useResourceTypes();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResourceType, setSelectedResourceType] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Handle opening modal for create
  const handleCreateNew = () => {
    setSelectedResourceType(null);
    setSaveError(null);
    setModalOpen(true);
  };

  // Handle opening modal for edit
  const handleEdit = (row) => {
    setSelectedResourceType(row);
    setSaveError(null);
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedResourceType(null);
    setSaveError(null);
  };

  // Handle save from modal (create or update)
  const handleSave = async (data) => {
    try {
      setSaveError(null);
      if (selectedResourceType) {
        // Update existing
        await update(data);
      } else {
        // Create new
        await create(data);
      }
      handleModalClose();
    } catch (err) {
      console.error('Failed to save resource type:', err);
      setSaveError(err);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource type?')) {
      try {
        await remove(id);
      } catch (err) {
        console.error('Failed to delete resource type:', err);
        alert('Failed to delete resource type. Please try again.');
      }
    }
  };

  // Handle menu open
  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  // Handle menu edit
  const handleMenuEdit = () => {
    if (selectedRow) {
      handleEdit(selectedRow);
      handleMenuClose();
    }
  };

  // Handle menu delete
  const handleMenuDelete = () => {
    if (selectedRow) {
      handleDelete(selectedRow.id);
      handleMenuClose();
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => row.original.name,
      },
      {
        accessorKey: 'resourcePrefix',
        header: 'Prefix',
        cell: ({ row }) => (
          <Chip
            label={row.original.resourcePrefix}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, fontFamily: 'monospace' }}
          />
        ),
      },
      {
        accessorKey: 'icon',
        header: 'Icon',
        cell: ({ row }) => row.original.icon || '—',
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description || '—',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Chip
            label={row.original.status}
            color={row.original.status === 'ACTIVE' ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: 'domainRestrictions',
        header: 'Domain Restrictions',
        cell: ({ row }) => {
          const domains = row.original.domainRestrictions || [];
          if (domains.length === 0) return '—';
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {domains.map((domain) => (
                <Chip key={domain} label={domain} size="small" variant="outlined" />
              ))}
            </Stack>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Box display="flex" justifyContent="center">
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, row.original)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  // Table instance
  const table = useReactTable({
    data: resourceTypes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load resource types: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* White Background Container */}
      <Box sx={{
        backgroundColor: '#fff',
        borderRadius: 2,
        p: 3,
      }}>
        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Resource Types
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage resource types and their configurations.
            </Typography>
          </Box>
          <AdminButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Add Resource Type
          </AdminButton>
        </Box>

        {/* Table */}
        <Box sx={{
          overflowX: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#f5f5f5',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {/* Data rows */}
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                        fontSize: '0.875rem',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Empty state */}
              {table.getRowModel().rows.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{
                      padding: '48px 16px',
                      textAlign: 'center',
                      color: '#999',
                      backgroundColor: '#fff',
                    }}
                  >
                    No resource types found. Click "Add Resource Type" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        {/* Row Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Create/Edit Modal */}
        <ResourceTypeModal
          open={modalOpen}
          onClose={handleModalClose}
          onSave={handleSave}
          resourceType={selectedResourceType}
          isSaving={isCreating || isUpdating}
          saveError={saveError}
        />
      </Box>
    </Box>
  );
}

export default ResourceTypesTab;
